import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import React, { useEffect, useState } from 'react'
import useFetchData from '../../../../../hooks/useFetchData'
import Skeleton from '@mui/material/Skeleton'
import Alert from '@mui/material/Alert'
import { Checkbox, FormControlLabel } from '@mui/material'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import { useSnackbar } from 'notistack'
import { updateLicense } from '../../../../../services/tenant/tenant.service'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSelector } from 'react-redux'
import DialogConfirmCancel from '../../../../dialog/DialogConfirm'
import { syncLicenses } from '../../../../../services/sync/sync.service'
import { replaceDash } from '../../../../../utils/text'
import { handlerErrorV2 } from '../../../../../utils/errors'

const License = ({ permission, licenseId = [] }) => {
  const { isLoading, data } = useFetchData('license')

  const { enqueueSnackbar } = useSnackbar()
  const [form, setInputForm] = useState({ licenseId })
  const [error, setInputErrors] = useState({})
  const [loading, toggleLoading] = useState(false)
  const [modal, setModal] = useState(null)
  const sync = useSelector((state) => state?.session?.permissions.sync || {})
  useEffect(() => {
    setInputForm({ licenseId })
  }, [licenseId])

  const onSave = async (e) => {
    e.preventDefault()
    setInputErrors({})

    try {
      toggleLoading(true)
      const result = await updateLicense(form)
      if (!result.data && result.error) {
        setInputErrors({ message: result.error.message || 'Something went wrong. Please try again.' })
      } else {
        enqueueSnackbar('Updated successfully', { variant: 'success' })
      }
    } catch (error) {
      toggleLoading(false)
      const message = handlerErrorV2(error)
      setInputErrors({ message })
    } finally {
      toggleLoading(false)
    }
  }

  const handleChange = (event) => {
    const { checked, value } = event.target
    let data = new Set(form.licenseId)
    if (checked) {
      data.add(value)
    } else {
      data.delete(value)
    }
    setInputForm({ ...form, licenseId: Array.from(data) })
  }

  const onSync = async (e) => {
    e.preventDefault()
    setInputErrors({})

    try {
      toggleLoading(true)
      const result = await syncLicenses(form)
      if (!result.data && result.error) {
        setInputErrors({ message: result.error.message || 'Something went wrong. Please try again.' })
      } else {
        setModal(null)
        enqueueSnackbar('Sync successfully', { variant: 'success' })
      }
    } catch (error) {
      toggleLoading(false)
      const message = handlerErrorV2(error)
      setInputErrors({ message })
    } finally {
      toggleLoading(false)
    }
  }

  return (
    <>
      {modal && (
        <DialogConfirmCancel
          id='sync-license'
          open={modal}
          onCancel={() => setModal(null)}
          onConfirm={onSync}
          loading={loading}
          title='Sync'
          maxWidth='xs'
        >
          Are you sure you want to sync licenses?
        </DialogConfirmCancel>
      )}
      <Box className='bg-primary text-primary-foreground px-4 py-1 flex gap-4 items-center justify-between h-8'>
        <Typography variant='subtitle1' className='uppercase'>
          License
        </Typography>
        <div className='flex justify-end items-center w-full gap-2'>
          {!isLoading && sync?.sync && (
            <ButtonStartIcon
              onClick={() => setModal('sync')}
              startIcon='sync'
              color='secondary'
              bGcolor='secondary'
              disabled={isLoading}
            >
              Sync
            </ButtonStartIcon>
          )}
          {!isLoading && permission && (
            <ButtonStartIcon
              onClick={onSave}
              color='secondary'
              loading={loading}
              variant='contained'
              startIcon={['fal', 'check']}
            >
              Save
            </ButtonStartIcon>
          )}
        </div>
      </Box>
      <Box className='px-2 py-2'>
        <Grid container spacing={1}>
          {isLoading
            ? Array(4)
                .fill()
                .map((_, i) => (
                  <Grid item md={3} sm={6} key={i} className='h-16'>
                    <Skeleton height={'100%'} />
                  </Grid>
                ))
            : data
                ?.sort((a, b) => a.name - b.name)
                .map((item) => (
                  <Grid item lg={2} md={3} sm={6} key={item._id}>
                    <Paper className='overflow-hidden'>
                      <div className='px-4 py-0 '>
                        {permission ? (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={form?.licenseId.includes(item._id)}
                                onChange={item.isDefault ? () => {} : handleChange}
                                name={item._id}
                                value={item._id}
                                disabled={loading}
                                color='primary'
                              />
                            }
                            label={
                              <Typography variant='inherit' color='primary'>
                                {item.name}
                              </Typography>
                            }
                          />
                        ) : (
                          <div className='flex items-center gap-2 py-4'>
                            <FontAwesomeIcon
                              icon={
                                form?.licenseId.includes(item._id)
                                  ? ['fas', 'square-check']
                                  : ['far', 'square']
                              }
                              className='text-primary text-xl'
                            />
                            <Typography variant='inherit' color='primary'>
                              {replaceDash(item.name)}
                            </Typography>
                          </div>
                        )}
                      </div>
                      {permission && (
                        <div className='pb-4 px-4 flex flex-col gap-1 text-muted-foreground'>
                          {item?.modules?.sort()?.map((feature) => (
                            <div key={`license-${item._id}-feature-${feature}`}> {replaceDash(feature)}</div>
                          ))}
                        </div>
                      )}
                    </Paper>
                  </Grid>
                ))}
          {error.message && (
            <Grid item md={12}>
              <Alert severity='error'>{error.message}</Alert>
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  )
}

export default License
